<template>
  <div>
    <TopBar />

    <main class="mx-auto mt-10 md:pr-0">
      <div class="mb-3 px-5">
        <NuxtLink :to="`/${$route.params.destination}/${$route.params.type}`" class="text-lg text-body">
          &lt; Back to {{ destination.name }}
        </NuxtLink>
      </div>

      <div class="mb-6 overflow-x-hidden" :style="{ 'background-color': venue.photos_prominent_colors && venue.photos_prominent_colors[0] }">
        <VenueCarousel :venue="venue" />
      </div>

      <div class="mx-auto max-w-6xl px-4 lg:flex">
        <div class="grow lg:pr-32">
          <div class="mb-1 flex justify-between">
            <NuxtLink v-if="venue.experts_choice" to="/experts-choice-awards" class="font-bw-gradual inline-block text-sm font-bold text-blue">
              <img class="-mt-1 mr-1 inline-block w-12" src="~/assets/images/2024stars.svg" alt="">
              Experts' Choice Award
            </NuxtLink>
            <div v-else>
              &nbsp;
            </div>
          </div>

          <h1 class="mb-4 block text-2rem font-bold text-slate md:text-39px">
            {{ venue.name }}
          </h1>

          <div class="flex justify-between">
            <div>
              <div class="mb-2">
                <a href="#" class="action-btn inline-flex items-center rounded-lg px-3 py-2 text-lg text-body" @click.prevent="showShare = true">
                  <svg
                    class="mr-3 inline-block"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.5 0L14.9133 4.3732L13.5867 6.01539L10.5556 3.56667V13.4873H8.44444V3.56667L5.41333 6.01539L4.08667 4.3732L9.5 0ZM0 11.5276H2.11111V16.8054H16.8889V11.5276H19V18.9165H0V11.5276Z" fill="#808080" />
                  </svg>
                  Share
                </a>
              </div>
              <div>
                <VenueFavorite
                  :venue="venue"
                  color="#808080"
                  text="Save"
                  btn-class="action-btn px-3 py-2 rounded-lg"
                  :icon-width="20"
                  :icon-height="18"
                />
              </div>
              <div v-if="venue.rank_as_percentage" class="mb-2 ml-1 mt-3 text-body">
                Top {{ venue.rank_as_percentage }}% of {{ venueType.name.toLowerCase() }}s in {{ destination.name }}
              </div>
            </div>
            <div class="mb-4 mt-6 md:mt-2">
              <VenueRating :venue="venue" />
            </div>
          </div>

          <hr class="mb-6">

          <SectionTitle v-model="showReviews">
            {{ venue.reviews.length }} expert {{ $f.pluralize('review', venue.reviews.length) }}
          </SectionTitle>

          <div v-if="showReviews" class="lg:mt-14">
            <div v-if="featuredReview" class="mb-14">
              <p class="mb-4 max-w-2xl text-2xl italic leading-normal text-gray-dark lg:text-4xl">
                <span class="lg:leading-snug" style="background: linear-gradient(to top, #F8FDCD 50%, transparent 50%); letter-spacing: -0.6px;">
                  “{{ featuredReview.extract }}”
                </span>
              </p>
              <p class="text-xl text-gray-dark lg:text-2xl">
                – {{ featuredReview.publication_name }}
              </p>
            </div>

            <VenueReviews :reviews="venue.reviews.filter(r => !r.featured_extract)" />
          </div>

          <hr v-if="venue.user_reviews" class="my-6">

          <VenueUserReviews v-if="venue.user_reviews" :user-reviews="venue.user_reviews" />

          <div v-if="['sm', 'md'].includes($screen.name)" class="mb-10 lg:hidden">
            <hr class="my-6">
            <VenueInfoBlock :venue="venue" class="max-w-100" />
          </div>

          <hr class="my-6">

          <div v-if="venue.influencer_reviews && venue.influencer_reviews.length">
            <VenueInfluencerReviews :venue="venue" />
            <hr class="my-6">
          </div>

          <div v-if="venue.amenities && venue.amenities.length">
            <VenueAmenities :venue="venue" />
            <hr class="my-6">
          </div>

          <div v-observe-visibility="{ callback: onNearbyVisible, once: true }">
            <div v-if="venuesNearby.length || loadingNearby">
              <VenueNearby :venue="venue" />
              <hr class="my-6">
            </div>
          </div>

          <div v-if="venue.featured_articles && venue.featured_articles.length">
            <VenueFeaturedIn :venue="venue" />
            <hr class="my-6">
          </div>
        </div>
        <div class="shrink-0 lg:w-80">
          <div class="top-20 md:sticky">
            <BookNow v-if="venue.venue_type_id === 1 && venue.booking_url" :venue="venue" />
            <VenueInfoBlock v-if="['lg', 'xl'].includes($screen.name)" :venue="venue" class="hidden lg:block xl:block" />
          </div>
        </div>
      </div>
    </main>
    <MainFooter />
    <ClientOnly>
      <ShareModal v-model="showShare" />
    </ClientOnly>

    <ExitIntentModal v-if="venue.venue_type_id === 1 && venue.booking_url" v-model="showExitIntent" :venue="venue" />
    <div ref="exitModalFake" />
  </div>
</template>

<script>
import ouibounce from 'ouibounce'
import { mapActions, mapState } from 'pinia'
import venueTypes from '~/config/venue-types.json'
import VenueReviews from '~/components/venue/VenueReviews'
import VenueAmenities from '~/components/venue/VenueAmenities'
import VenueFeaturedIn from '~/components/venue/VenueFeaturedIn'
import VenueNearby from '~/components/venue/VenueNearby'
import VenueRating from '~/components/venue/VenueRating'
import VenueCarousel from '~/components/venue/VenueCarousel'
import BookNow from '~/components/venue/BookNow'
import VenueFavorite from '~/components/shared/VenueFavorite'
import TopBar from '~/components/layout/TopBar'
import MainFooter from '~/components/layout/MainFooter'
import ExitIntentModal from '~/components/venue/ExitIntentModal'
import SectionTitle from '~/components/venue/SectionTitle'
import VenueUserReviews from '~/components/venue/VenueUserReviews'
import ShareModal from '~/components/shared/ShareModal'
import VenueInfoBlock from '~/components/venue/VenueInfoBlock'
import VenueInfluencerReviews from '~/components/venue/VenueInfluencerReviews.vue'
import { useVenueStore } from '~/stores/venue'
import { useDestinationsStore } from '~/stores/destinations'
import { useCurrenciesStore } from '~/stores/currencies'
import { showError, useHead, useNuxtApp, useRoute, useRuntimeConfig } from '#imports'
import { callWithNuxt } from '#app'
import { useLoadDestination } from '~/composables/useLoadDestination'

export default {
  components: { VenueInfluencerReviews, VenueUserReviews, SectionTitle, ExitIntentModal, ShareModal, VenueCarousel, VenueRating, VenueNearby, VenueFeaturedIn, VenueAmenities, VenueReviews, BookNow, VenueFavorite, TopBar, MainFooter, VenueInfoBlock },

  async setup () {
    const route = useRoute()
    const { params } = route
    const app = useNuxtApp()
    const type = params.type.replace(/s$/, '')
    const venueStore = useVenueStore()

    const venueType = venueTypes.find(v => v.permalink === type)
    if (! venueType) {
      return showError({ statusCode: 404, statusMessage: 'Venue type not found' })
    }

    await useLoadDestination()

    const path = `${params.destination}/${params.type}/${params.name}`
    const shouldLoadVenue = venueStore.venue.data.path !== path
    if (shouldLoadVenue) {
      try {
        venueStore.clearNearby()
        await callWithNuxt(app, () => venueStore.loadVenue(path))
      } catch (e) {
        // if (! e.response || (e.response && e.response.status !== 404)) { app.$sentry.captureException(e) }
      }
    }
    if (! venueStore.venue.data.id) {
      return showError({ statusCode: 404, statusMessage: 'Venue not found' })
    }

    const venue = venueStore.venue.data

    let preload = ['sm', 'md'].includes(app.$screen.name)
      ? venue.photos.map((p, idx) => idx === 0 ? p : null)
      : venue.photos.map((p, idx) => [0, 1, 2, venue.photos.length - 1, venue.photos.length - 2].includes(idx) ? p : null)
    preload = preload.filter(p => p && (p.photo_rectangular_webp || p.photo_rectangular))

    callWithNuxt(app, () => useHead({
      title: `${venue.name} Reviews | Tripexpert`,
      meta: [
        { hid: 'description', name: 'description', content: venue.meta_description },
        { hid: 'og:locale', property: 'og:locale', content: 'en_US' },
        { hid: 'og:title', property: 'og:title', content: `${venue.name}` },
        { hid: 'og:description', property: 'og:description', content: venue.meta_description },
        { hid: 'og:image', property: 'og:image', content: venue.photos.length && venue.photos[0].photo_rectangular },
        { hid: 'og:url', property: 'og:url', content: `${useRuntimeConfig().public.appHost}${route.fullPath}` },
        { hid: 'twitter:card', property: 'twitter:card', content: 'summary' },
        { hid: 'twitter:site', property: 'twitter:site', content: '@tripexpert' },
        { hid: 'twitter:title', property: 'twitter:title', content: venue.name },
        { hid: 'twitter:description', property: 'twitter:description', content: venue.meta_description },
        { hid: 'twitter:image', property: 'twitter:image', content: venue.photos.length && venue.photos[0].photo_rectangular },
      ],

      link: [
        ...preload.map(p => ({ rel: 'preload', href: p.photo_rectangular_webp || p.photo_rectangular, as: 'image' })),
      ],
    }))
  },

  data: () => ({
    venueTypes,
    showReviews: true,
    showAmenities: false,
    showNearby: false,
    showArticles: false,
    showShare: false,
    showExitIntent: false,
  }),

  computed: {
    ...mapState(useVenueStore, {
      venue: s => s.venue.data,
      loadingVenue: s => s.venue.loading,
      loadingNearby: s => s.nearby.loading,
      venuesNearby: s => s.nearby.data,
    }),

    ...mapState(useDestinationsStore, {
      destination: s => s.destination.data,
    }),

    ...mapState(useCurrenciesStore, {
      currencies: s => s.currencies.data,
    }),

    venueType () {
      const type = this.$route.params.type.replace(/s$/, '')
      return this.venueTypes.find(v => v.permalink === type)
    },

    featuredReview () {
      return this.venue.reviews.find(r => r.featured_extract)
    },
  },

  mounted () {
    if (this.$config.intentLayoutVenue) {
      ouibounce(this.$refs.exitModalFake, {
        cookieName: 'exitModalVenueShown',
        cookieExpire: 0.5,
        callback: () => {
          this.showExitIntent = true
        },
      })
    }
  },

  methods: {
    ...mapActions(useVenueStore, ['loadVenue', 'loadNearby']),
    ...mapActions(useDestinationsStore, ['loadDestination']),

    onNearbyVisible (visible) {
      if (visible) { this.loadNearby() }
    },
  },

  jsonld () {
    return {
      '@context': 'https://schema.org',
      '@type': 'AggregateRating',
      'itemReviewed': {
        '@type': this.venueType.name === 'Attraction' ? 'LocalBusiness' : this.venueType.name,
        'image': this.venue.photos.length ? this.venue.photos[0].photo_rectangular : null,
        'name': this.venue.name,
        // servesCuisine: 'Seafood',
        // priceRange: '$$$',
        'telephone': this.venue.telephone || '',
        'address': this.venue.address,
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': this.venue.latitude,
          'longitude': this.venue.longitude,
        },
      },

      'ratingValue': this.venue.aggregate_rating?.rating_value,
      'bestRating': Number(this.venue.aggregate_rating?.best_rating),
      'ratingCount': this.venue.aggregate_rating?.rating_count,
    }
  },
}
</script>

<style lang="scss" scoped>
.action-btn:hover, :deep(.action-btn:hover) {
  @apply text-blue bg-gray-light;
  path { fill: #4348DB; }
}
</style>
